import {isNullOrZero} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import * as MeasurementConstants from 'constants/MeasurementConstants';
import {ContainerSummary} from 'constants/SampleConstants';
import * as InventoryUnitUtil from 'inventory/InventoryUnitUtil';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {getContainerLabelWithQuantity} from 'samples/SampleUtil';
import {
  ConfirmTransferQuantitiesDataRow,
  ConfirmTransferQuantitiesPaneState,
  UpdateTransferModalStateHandler,
} from 'samples/transfers/TransferModalUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {Title} from 'ui-library/Headers';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import BulkSetQuantityUnitsDropdown from './BulkSetQuantityUnitsDropdown';
import BulkSetValuesDropdown from './BulkSetValuesDropdown';
import ConfirmTransferQuantityRow from './ConfirmTransferQuantityRow';
import InputContainableConcentrationForm from './InputContainableConcentrationForm';
import InputContainerQuantityChange from './InputContainerQuantityChange';
import * as TransferUtil from './TransferUtil';

interface IncompatibleUnitsRowInfo {
  rowIndex: number;
  inputContainer: ContainerSummary | null;
  transferQuantity: MeasurementConstants.LegacyMeasurement;
  initialOutputContainerState: ContainerSummary;
}

export function getIncompatibleUnitsRowInfos({
  selectedInputContainer,
  confirmTransferQuantitiesData,
  initialOutputContainerStateById,
}: ConfirmTransferQuantitiesPaneState): Array<IncompatibleUnitsRowInfo> {
  if (confirmTransferQuantitiesData == null) {
    return [];
  }
  const incompatibleRowInfos = confirmTransferQuantitiesData.map((row, i) => {
    const inputUnits = selectedInputContainer?.quantity.units ?? null;
    const transferUnits = row.outputContainer.quantity.units;
    const initialOutputContainerState = initialOutputContainerStateById[row.outputContainer.id];
    assert(
      initialOutputContainerState != null,
      `Cannot find original output container state for ${row.outputContainer.barcode}`
    );
    const originalOutputUnits = initialOutputContainerState.quantity.units;
    if (InventoryUnitUtil.areUnitsCompatibleFromSymbols([inputUnits, transferUnits, originalOutputUnits])) {
      return null;
    }
    return {
      rowIndex: i,
      inputContainer: selectedInputContainer,
      initialOutputContainerState,
      transferQuantity: {value: row.transferQuantityValue, units: transferUnits},
    };
  });
  // Use flatMap instead of filter so typescript can infer the return type correctly :(
  return incompatibleRowInfos.flatMap((rowInfo) => (rowInfo != null ? [rowInfo] : []));
}

export function ConfirmTransferQuantitiesIncompatibleUnitsError({
  incompatibleUnitRowInfos,
}: {
  incompatibleUnitRowInfos: Array<IncompatibleUnitsRowInfo>;
}): JSX.Element {
  const rowErrorMessages = incompatibleUnitRowInfos.map(
    ({rowIndex, inputContainer, transferQuantity, initialOutputContainerState}) => {
      const errorMessagePrefix = `Row ${rowIndex + 1} has incompatible units: Cannot transfer `;
      const transferredQuantityText =
        transferQuantity.units != null ? `${transferQuantity.value ?? 0} ${transferQuantity.units} ` : '';
      const inputContainerText = inputContainer
        ? `from ${getContainerLabelWithQuantity(inputContainer)} `
        : '';
      const outputContainerText = `into ${getContainerLabelWithQuantity(initialOutputContainerState)}`;
      return `${errorMessagePrefix}${transferredQuantityText}${inputContainerText}${outputContainerText}`;
    }
  );
  return (
    <div className={`legacy-font-proxima-13 ${css(styles.errorMessage)}`}>
      <p className="u-danger-color">{rowErrorMessages.filter((rowError) => rowError != null).join('\n')}</p>
    </div>
  );
}

interface Props {
  paneState: ConfirmTransferQuantitiesPaneState;
  onUpdateModalState: UpdateTransferModalStateHandler;
}

function ConfirmTransferQuantitiesPane(props: Props): JSX.Element {
  const {
    confirmTransferQuantitiesData,
    inputContainable,
    inputContainableConcentration,
    selectedInputContainer,
    errorMessage,
    initialOutputContainerStateById,
  } = props.paneState;
  if (!confirmTransferQuantitiesData) {
    return (
      <div className="u-spacing-top text-center">
        <HorizontalThrobber />
      </div>
    );
  }

  function _handleUpdate(index: number, quantityData: Partial<ConfirmTransferQuantitiesDataRow>): void {
    const newConfirmTransferQuantitiesData = reactUpdate(confirmTransferQuantitiesData, {
      [index]: {$merge: quantityData},
    });
    props.onUpdateModalState({confirmTransferQuantitiesData: newConfirmTransferQuantitiesData});
  }

  function _handleUpdateAll(
    mapRowFn: (row: ConfirmTransferQuantitiesDataRow) => ConfirmTransferQuantitiesDataRow
  ): void {
    const newConfirmTransferQuantitiesData = confirmTransferQuantitiesData!.map((container) =>
      mapRowFn(container)
    );
    props.onUpdateModalState({confirmTransferQuantitiesData: newConfirmTransferQuantitiesData});
  }

  function _handleUpdateAllTransferQuantities(value: number | null): void {
    _handleUpdateAll((row) => ({...row, transferQuantityValue: value}));
  }

  function _handleUpdateAllFinalQuantities(value: number | null): void {
    _handleUpdateAll((row) => ({...row, finalOutputQuantityValue: value}));
  }

  function _handleUpdateAllQuantityUnits(units: string | null): void {
    _handleUpdateAll((row) =>
      reactUpdate(row, {
        outputContainer: {
          volume: {
            $merge: TransferUtil.updateQuantityUnits(row.outputContainer.quantity, units),
          },
          quantity: {
            $merge: TransferUtil.updateQuantityUnits(row.outputContainer.quantity, units),
          },
        },
      })
    );
  }

  const incompatibleUnitsRowInfos = getIncompatibleUnitsRowInfos(props.paneState);

  return (
    <div>
      <Title>Transfer {getQuantityDisplayString(true, true)}</Title>
      <br />
      <Row className="transferModal-tableHeader">
        <Col xs={2}>Destination Barcode</Col>
        <Col xs={2}>Destination Name</Col>
        <Col xs={2}>{`Initial destination ${getQuantityDisplayString(false, false)}`}</Col>
        <Col xs={2}>
          <>{`${getQuantityDisplayString(true, false)} from source`}</>
          <BulkSetValuesDropdown
            onSetValue={(value: number | null) => {
              _handleUpdateAllTransferQuantities(value);
            }}
          />
        </Col>
        <Col xs={2}>
          <>{`Final destination ${getQuantityDisplayString(false, false)}`}</>
          {!inputContainable && (
            <BulkSetValuesDropdown
              onSetValue={(value: number | null) => {
                _handleUpdateAllFinalQuantities(value);
              }}
            />
          )}
        </Col>
        <Col xs={2}>
          <>Transfer units</>
          <BulkSetQuantityUnitsDropdown
            onSetUnits={(units: string | null) => {
              _handleUpdateAllQuantityUnits(units);
            }}
            allowNullUnits={confirmTransferQuantitiesData.every(({outputContainer}) =>
              isNullOrZero(outputContainer.quantity.value)
            )}
            outputContainerQuantityUnits={confirmTransferQuantitiesData.map(
              ({outputContainer}) => initialOutputContainerStateById[outputContainer.id]?.quantity.units
            )}
          />
        </Col>
      </Row>
      <div className="transferModal-tableRows">
        {confirmTransferQuantitiesData.map((row, i) => (
          <ConfirmTransferQuantityRow
            key={i}
            isInputTypeContainer={Boolean(selectedInputContainer)}
            finalOutputQuantityValue={row.finalOutputQuantityValue}
            index={i}
            onUpdate={(index: number, changes: Partial<ConfirmTransferQuantitiesDataRow>) => {
              _handleUpdate(index, changes);
            }}
            outputContainer={row.outputContainer}
            initialOutputContainerState={initialOutputContainerStateById[row.outputContainer.id]}
            transferQuantityValue={row.transferQuantityValue}
          />
        ))}
      </div>
      {inputContainable && inputContainableConcentration && (
        <InputContainableConcentrationForm
          inputContainable={inputContainable}
          inputContainableConcentration={inputContainableConcentration}
          onUpdateModalState={props.onUpdateModalState}
        />
      )}
      {selectedInputContainer && (
        <InputContainerQuantityChange
          confirmTransferQuantitiesData={confirmTransferQuantitiesData}
          inputContainer={selectedInputContainer}
        />
      )}
      {errorMessage && (
        <div className={`legacy-font-proxima-13 ${css(styles.errorMessage)}`}>
          <p className="u-danger-color">{`Error when attempting transfer: ${errorMessage}`}</p>
        </div>
      )}
      {incompatibleUnitsRowInfos.length > 0 && (
        <ConfirmTransferQuantitiesIncompatibleUnitsError
          incompatibleUnitRowInfos={incompatibleUnitsRowInfos}
        />
      )}
    </div>
  );
}

export default ConfirmTransferQuantitiesPane;

const styles = StyleSheet.create({errorMessage: {paddingTop: '4px', whiteSpace: 'pre-line'}});
